import { type CSSProperties, useId } from "react";
import { useTranslations } from "use-intl";

import { useCuratedCrateName } from "@/components/curated-crate-name";
import { Link } from "@/components/link";
import { Price } from "@/components/Price";
import type { CuratedCrateProof } from "@/features/garimpos/garimpos-query";
import { curatedCrateOffersSearch, type CuratedCrate } from "@garimpa/lib/curatedCrates";
import { buildImageSrcSet, withImageRenderParams } from "@garimpa/lib/imageUrls";

interface FanSlot {
  left: string;
  /** Resting transform of the cover in the fanned stack. */
  rest: string;
  /** Transform when the card is hovered or focused: the stack spreads open. */
  open: string;
}

const FAN_LAYOUTS: Record<number, FanSlot[]> = {
  1: [{ left: "22%", rest: "rotate(-2deg)", open: "translateY(-6px) rotate(-4deg)" }],
  2: [
    { left: "11%", rest: "rotate(-4deg)", open: "translate(-8px, -2px) rotate(-8deg)" },
    { left: "33%", rest: "rotate(4deg)", open: "translate(8px, -2px) rotate(8deg)" },
  ],
  3: [
    { left: "0%", rest: "rotate(-6deg)", open: "translateX(-10px) rotate(-10deg)" },
    { left: "22%", rest: "rotate(0deg)", open: "translateY(-6px) rotate(0deg)" },
    { left: "44%", rest: "rotate(6deg)", open: "translateX(12px) rotate(10deg)" },
  ],
  4: [
    { left: "0%", rest: "rotate(-6deg)", open: "translateX(-10px) rotate(-10deg)" },
    { left: "14.667%", rest: "rotate(-2deg)", open: "translate(-4px, -4px) rotate(-4deg)" },
    { left: "29.333%", rest: "rotate(2deg)", open: "translate(4px, -6px) rotate(4deg)" },
    { left: "44%", rest: "rotate(6deg)", open: "translate(12px, -2px) rotate(10deg)" },
  ],
};

const COVER_CLASS =
  "absolute top-[6%] aspect-square w-[56%] rounded-[4px] bg-secondary object-cover shadow-[0_6px_18px_rgba(0,0,0,0.55)] [transform:var(--fan-rest)] motion-safe:transition-transform motion-safe:duration-300 motion-safe:ease-[cubic-bezier(0.2,0.8,0.2,1)] motion-safe:group-hover:[transform:var(--fan-open)] motion-safe:group-focus-visible:[transform:var(--fan-open)]";

function fanStyle(slot: FanSlot): CSSProperties {
  return {
    left: slot.left,
    "--fan-rest": slot.rest,
    "--fan-open": slot.open,
  } as CSSProperties;
}

function CrateCovers({ covers }: { covers: CuratedCrateProof["covers"] }) {
  const shown = covers.slice(0, 4);
  const layout = FAN_LAYOUTS[shown.length];

  return (
    <div aria-hidden="true" className="relative mb-4 aspect-[25/16]">
      {layout ? (
        shown.map((cover, index) => (
          <img
            key={`${cover.imageUrl}-${index}`}
            src={withImageRenderParams(cover.imageUrl, { width: 320 })}
            srcSet={buildImageSrcSet(cover.imageUrl, [150, 320, 480])}
            sizes="(min-width: 640px) 11rem, 50vw"
            alt=""
            loading="lazy"
            decoding="async"
            className={COVER_CLASS}
            style={fanStyle(layout[index]!)}
          />
        ))
      ) : (
        <span className={COVER_CLASS} style={fanStyle(FAN_LAYOUTS[1]![0]!)} />
      )}
    </div>
  );
}

/** One curated crate: fanned real covers, name, live offer count and lowest price. */
export function CrateCard({ crate, proof }: { crate: CuratedCrate; proof: CuratedCrateProof }) {
  const t = useTranslations("garimpos");
  const crateName = useCuratedCrateName();
  const nameId = useId();
  const metaId = useId();

  return (
    <Link
      to="/offers"
      search={curatedCrateOffersSearch(crate)}
      aria-labelledby={nameId}
      aria-describedby={metaId}
      className="group flex h-full flex-col rounded-xl border border-border bg-card p-4 transition-colors hover:border-primary focus-visible:border-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
    >
      <CrateCovers covers={proof.covers} />
      <h3 id={nameId} className="text-balance text-lg font-bold leading-tight tracking-[-0.02em]">
        {crateName(crate)}
      </h3>
      <p
        id={metaId}
        className="mt-auto flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 pt-3 text-sm text-muted-foreground"
      >
        <span className="tabular-nums">{t("offerCount", { count: proof.totalCount })}</span>
        <span>
          {t.rich("fromPrice", {
            price: () => (
              <Price
                amount={proof.lowestPriceTotal}
                currency={proof.currency}
                className="text-base font-bold text-foreground"
              />
            ),
          })}
        </span>
      </p>
    </Link>
  );
}
